import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useHybridAuth } from '../pages/auth/hooks/useHybridAuth';
import { PageLoader } from '../shared/components/ui/Loader';
import type { User } from '../api/auth/schemas/auth';
import { ROUTES, USER_ROLES, hasPermission, PERMISSIONS, type UserRole } from '../shared/constants';
import { RoleViewProvider, useRoleView } from './RoleViewContext';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  updateUser: (user: User) => void;
  sendMagicLink: (email: string) => Promise<void>;
  startOAuthFlow: (provider: 'google' | 'microsoft' | 'apple') => Promise<void>;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useHybridAuth();

  // Handle post-auth redirects
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const returnUrl = params.get('return_url');
    
    if (auth.isAuthenticated && returnUrl) {
      // Clean up the URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('return_url');
      window.history.replaceState({}, '', newUrl.toString());
      
      // Redirect to the return URL
      navigate(decodeURIComponent(returnUrl), { replace: true });
    }
  }, [auth.isAuthenticated, location.search, navigate]);

  const handleLogin = async (token: string) => {
    try {
      await auth.login(token);
      // The redirect will be handled by the effect above
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await auth.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const value: AuthContextValue = {
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    login: handleLogin,
    logout: handleLogout,
    signUp: auth.signup,
    loginWithGoogle: () => auth.startOAuthFlow('google'),
    loginWithMicrosoft: () => auth.startOAuthFlow('microsoft'),
    loginWithApple: () => auth.startOAuthFlow('apple'),
    updateUser: (user: User) => {
      // Update user in auth state
      console.log('updateUser not implemented', user);
    },
    sendMagicLink: auth.sendMagicLink,
    startOAuthFlow: auth.startOAuthFlow,
    refetch: auth.refetch,
  };

  return (
    <AuthContext.Provider value={value}>
      <RoleViewProvider userRole={auth.user?.role as UserRole || null}>
        {children}
      </RoleViewProvider>
    </AuthContext.Provider>
  );
}

// Protected route component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean; // If false, skip onboarding check (for onboarding page itself)
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireOnboarding = true
}) => {
  const { isAuthenticated, isLoading, user, refetch } = useAuth();
  const location = useLocation();
  const [isRefetching, setIsRefetching] = useState(false);

  // Refetch user data when route changes to ensure fresh data
  useEffect(() => {
    const refreshUserData = async () => {
      if (isAuthenticated && !isLoading) {
        setIsRefetching(true);
        await refetch();
        setIsRefetching(false);
      }
    };

    refreshUserData();
  }, [location.pathname, isAuthenticated, isLoading, refetch]);

  if (isLoading || isRefetching) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    // Preserve the attempted URL for redirect after login
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?return_url=${returnUrl}`} replace />;
  }

  // Check onboarding completion from user object
  const hasCompletedOnboarding = user?.onboarding_completed === true;

  // If user needs onboarding and hasn't completed it, redirect to onboarding
  if (requireOnboarding && !hasCompletedOnboarding && location.pathname !== ROUTES.ONBOARDING) {
    return <Navigate to={ROUTES.ONBOARDING} replace />;
  }

  // If user completed onboarding and is on onboarding page, redirect to dashboard
  if (hasCompletedOnboarding && location.pathname === ROUTES.ONBOARDING) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
};

// Admin-only route component
interface AdminRouteProps {
  children: React.ReactNode;
}

export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { effectiveRole } = useRoleView();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if effective role is admin (respects role view impersonation)
  console.log('AdminRoute: Checking effective role', { effectiveRole, actualRole: user?.role });
  if (effectiveRole !== USER_ROLES.ADMIN) {
    // Redirect non-admin users to question demo
    return <Navigate to={ROUTES.QUESTION_DEMO} replace />;
  }

  return <>{children}</>;
};

// Permission-based route component
interface PermissionRouteProps {
  children: React.ReactNode;
  permission: keyof typeof PERMISSIONS;
  redirectTo?: string;
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  permission,
  redirectTo = ROUTES.QUESTION_DEMO
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { effectiveRole } = useRoleView();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if effective role has permission (respects role view impersonation)
  if (!hasPermission(effectiveRole || undefined, permission)) {
    console.log(`PermissionRoute: Effective role lacks permission ${permission}`, { effectiveRole, actualRole: user?.role });
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};